export interface InkPoint {
  x: number;
  y: number;
  /** 0-1. Pen pressure; synthesised to 0.5 for mouse/touch. */
  pressure: number;
  tiltX: number;
  tiltY: number;
  /** ms since engine start. */
  t: number;
}

export type InkTool = 'pen' | 'highlighter' | 'eraser' | 'lasso';

export interface Stroke {
  id: string;
  points: InkPoint[];
  color: string;
  /** Nominal width in px at full pressure. */
  width: number;
  tool: InkTool;
  /** True when the stroke came from an actual stylus, not a finger or mouse. */
  fromPen: boolean;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function strokeBounds(stroke: Stroke): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of stroke.points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

export function unionBounds(list: Bounds[]): Bounds {
  return list.reduce(
    (acc, b) => ({
      minX: Math.min(acc.minX, b.minX),
      minY: Math.min(acc.minY, b.minY),
      maxX: Math.max(acc.maxX, b.maxX),
      maxY: Math.max(acc.maxY, b.maxY),
    }),
    { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
  );
}

export function boundsWidth(b: Bounds): number {
  return b.maxX - b.minX;
}

export function boundsHeight(b: Bounds): number {
  return b.maxY - b.minY;
}

export function boundsCenter(b: Bounds): { x: number; y: number } {
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
}

/** Total path length of a stroke. */
export function strokeLength(stroke: Stroke): number {
  let total = 0;
  for (let i = 1; i < stroke.points.length; i++) {
    const a = stroke.points[i - 1];
    const b = stroke.points[i];
    total += Math.hypot(b.x - a.x, b.y - a.y);
  }
  return total;
}

/** Distance between first and last point, relative to path length: near 0 = closed loop. */
export function closure(stroke: Stroke): number {
  if (stroke.points.length < 3) return 1;
  const first = stroke.points[0];
  const last = stroke.points[stroke.points.length - 1];
  const gap = Math.hypot(last.x - first.x, last.y - first.y);
  const len = strokeLength(stroke);
  return len === 0 ? 1 : gap / len;
}
